// Portfolio content for Bagus A Kurniawan
import { Code2, Users, ShieldCheck, Target } from "lucide-react";

const now = new Date();
const startYear = 2013;
const yearsOfExperience = now.getFullYear() - startYear;

export const personalInfo = {
  name: "Bagus A Kurniawan",
  username: "GoodOne",
  tagline: "I build backend systems.",
  subtitle:
    "Currently focused on high-throughput APIs, event-driven pipelines, and the reliability work that keeps them running under load.",
  email: "hello@goodone.dev",
  location: "Malang, Indonesia",
  availability: "Open to work",
  linkedin: "https://www.linkedin.com/in/BagusAK95",
  github: "https://github.com/goodone-dev"
};

export const aboutMe = {
  intro:
    `Based in Malang, Indonesia. For the past ${yearsOfExperience} years I've built distributed systems for products in e-commerce, NFT launchpad, blockchain, and conversational AI — mostly with Go and Node.js on top of Postgres, RabbitMQ, and Redis.`,
  approach:
    "I favour pragmatic solutions over clever ones, DDD where it earns its weight, and progressive rewrites over big-bang ones. Boring systems that keep running beat clever ones that don't."
};

export const highlights = [
  {
    icon: Code2,
    title: `${yearsOfExperience}+ years shipping`,
    description: "Backend systems across e-commerce, NFT launchpad, blockchain, and conversational AI."
  },
  {
    icon: Users,
    title: "Team leadership",
    description: "Ten-plus engineers mentored — pairing, code review, and technical direction."
  },
  {
    icon: ShieldCheck,
    title: "Reliability",
    description: "The unglamorous work — observability, retries, back-pressure — that keeps systems up."
  },
  {
    icon: Target,
    title: "Business fit",
    description: "Translating product goals into systems that survive contact with production."
  }
];

export const skills = {
  languages: ["Go", "Node.js", "TypeScript", ".NET", "PHP"],
  backend: ["Gin", "Echo", "Chi", "Fiber", "NestJS", "Express", "GraphQL", "REST", "gRPC"],
  databases: ["PostgreSQL", "MySQL", "CockroachDB", "Oracle", "MongoDB", "Elasticsearch", "Redis", "Memcached"],
  messageQueue: ["RabbitMQ", "Apache Kafka", "Redis Pub/Sub"],
  devops: ["Docker", "CI/CD", "Observability"],
  leadership: ["System architecture", "Technical strategy", "Mentorship", "Cross-functional leadership"],
  specializations: ["Event-driven architecture", "CQRS & event sourcing", "Performance engineering", "Distributed systems"],
  summary: {
    experience: `${yearsOfExperience}+`,
    project: "20+",
    mentored: "10+"
  }
};

export const experience = [
  {
    id: 1,
    company: "eDOT",
    position: "Senior Backend Engineer",
    duration: "Oct 2025 — Apr 2026",
    location: "Bandung, ID",
    achievements: [
      "Building backend microservices and REST APIs in Go with an emphasis on throughput and predictable latency.",
      "Tuning MongoDB queries and tightening consistency guarantees where the domain calls for it.",
      "Adding structured logging, distributed tracing, and consistent error handling so incidents can be traced instead of guessed at."
    ],
    technologies: ["REST", "gRPC", "Go", "Fiber", "MongoDB", "RabbitMQ", "Redis"]
  },
  {
    id: 2,
    company: "Catalyst",
    position: "Senior Backend Engineer",
    duration: "Jun 2022 — Apr 2025",
    location: "Jakarta, ID",
    achievements: [
      "Led a microservices rewrite alongside frontend and mobile, and cut cross-team integration bugs noticeably in the process.",
      "Reshaped how we ran Scrum — shorter meetings, clearer priorities — so the team stopped burning out on process.",
      "Automated most of the recurring BAU requests that used to eat engineering time each week.",
      "Ran Clean Architecture reviews on new services so the layering held up as the team grew, which shortened onboarding for incoming engineers.",
      "Rolled out metrics, tracing, and structured logging across the microservices so incidents could be traced instead of guessed at."
    ],
    technologies: ["REST", "GraphQL", "Go", "Chi", "PostgreSQL", "MongoDB", "Redis", "RabbitMQ", "Docker"]
  },
  {
    id: 3,
    company: "Gaspack",
    position: "Backend Engineer (Part-time)",
    duration: "May 2022 — Sep 2023",
    location: "Jakarta, ID",
    achievements: [
      "Built an NFT generator that turned client asset libraries into unique tokens automatically, cutting the manual work per collection by around 80%.",
      "Wrote the API layer that talked to Web3 smart contracts, handling signing, minting, and the retry logic around chain flakiness.",
      "Set up the cloud deployment with load balancing and async workers so the generator could absorb spiky launch-day traffic without falling over."
    ],
    technologies: ["REST", "Node.js", "TypeScript", "NestJS", "PostgreSQL", "MongoDB", "RabbitMQ", "Redis"]
  },
  {
    id: 4,
    company: "Kata.ai",
    position: "Senior Backend Engineer",
    duration: "Dec 2018 — Jun 2022",
    location: "Jakarta, ID",
    achievements: [
      "Shipped features on the chatbot builder that materially moved platform adoption during my time there.",
      "Led the omnichannel platform work — routing conversations between users, bots, and human agents across channels.",
      "Refactored and migrated older microservices, mostly to bring latency down as user load grew.",
      "Pushed for stricter service boundaries in code review — fewer cross-service imports, less shared state — which paid off when we needed to peel services apart later."
    ],
    technologies: ["REST", "Go", "Fiber", "Node.js", "TypeScript", "NestJS", "PostgreSQL", "CockroachDB", "MongoDB", "RabbitMQ", "Redis", "Docker"]
  },
  {
    id: 5,
    company: "Labtek Indie",
    position: "Backend Engineer (Freelance)",
    duration: "Nov 2021 — Mar 2022",
    location: "Bandung, ID",
    achievements: [
      "Rewrote the API layer for an existing platform, mostly to make the backend easier to change without breaking the surface."
    ],
    technologies: ["REST", "Go", "Echo", "PostgreSQL", "Redis", "Docker"]
  },
  {
    id: 6,
    company: "Coinjoss",
    position: "Backend Engineer (Part-time)",
    duration: "Jan 2021 — Feb 2022",
    location: "Bali, ID",
    achievements: [
      "Built and integrated the APIs that stitched together wallets, the trading engine, and real-time market data for a crypto exchange."
    ],
    technologies: ["REST", "Node.js", "TypeScript", "NestJS", "PostgreSQL", "Redis", "Docker"]
  },
  {
    id: 7,
    company: "Ivosight",
    position: "Backend Engineer",
    duration: "Oct 2017 — Dec 2018",
    location: "Jakarta, ID",
    achievements: [
      "Built a crawler and sentiment pipeline for social content that clients used to steer campaign decisions.",
      "Wrote an automated web monitor that flagged content changes in near real time, replacing a manual review process."
    ],
    technologies: ["Node.js", "AdonisJS", "PostgreSQL", "MongoDB", "Redis"]
  },
  {
    id: 8,
    company: "UBIG",
    position: "Junior Backend Engineer",
    duration: "Jun 2013 — Oct 2017",
    location: "Malang, ID",
    achievements: [
      "Built marketplace crawlers that turned messy product listings into structured pricing and trend data.",
      "Wrote the distribution pipeline that moved that data into client databases on a reliable cadence.",
      "Built the API behind BTN Properti, a digital property marketplace connecting the app, listings, and third-party services.",
      "Built the API behind EzyPay, a bill-payment and top-up platform integrated with multiple payment providers.",
      "Wrote the automation layer for EzyPay transactions so payments and top-ups ran reliably at scale."
    ],
    technologies: ["REST", ".NET", "MySQL", "Oracle", "MongoDB", "Elasticsearch", "Memcached", "RabbitMQ"]
  }
];

export const projects = [
  {
    id: 1,
    title: "PaperJet",
    type: "Open-source · Desktop app",
    subtitle: "A lightweight, offline-first API client for developers who prefer native performance.",
    description:
      "PaperJet is a desktop API client designed for developers who want a fast, distraction-free way to organise, test, and debug HTTP requests — without relying on a browser or a cloud account. Requests live in workspaces and collections, folders keep things tidy, environment variables handle staging vs production, and everything is stored locally so it works fully offline.",
    technologies: ["Go", "Wails", "React", "SQLite", "GORM"],
    features: [
      "Workspace & collection management",
      "Folder & request hierarchy",
      "Environment variables",
      "Built-in HTTP proxy",
      "Offline-first with local SQLite storage",
      "Native desktop (macOS/Windows/Linux)"
    ],
    github: "https://github.com/goodone-dev/paperjet",
    image: "https://opengraph.githubassets.com/b583965fcca90f766c06d9c0272222e6c7b287fd42b4360d64b0c3279cb80ba1/goodone-dev/paperjet"
  },
  {
    id: 2,
    title: "Go Boilerplate",
    type: "Open-source · Go framework",
    subtitle: "A production-ready Go starter for scalable, observable backend services.",
    description:
      "A Go REST API boilerplate built around Clean/DDD layering so business code stays separate from infrastructure. It bundles multi-database support, structured observability, async messaging, and a containerised dev setup — meant to skip the tedious first week of a new service.",
    technologies: ["Go", "Redis", "PostgreSQL", "MySQL", "MongoDB", "RabbitMQ", "Docker"],
    features: [
      "Multi-database support",
      "Clean/DDD architecture",
      "Built-in observability",
      "REST + async messaging",
      "Containerised environment"
    ],
    github: "https://github.com/goodone-dev/go-boilerplate",
    image: "https://opengraph.githubassets.com/b583965fcca90f766c06d9c0272222e6c7b287fd42b4360d64b0c3279cb80ba1/goodone-dev/go-boilerplate"
  },
  {
    id: 3,
    title: "Thief CLI",
    type: "Open-source · Node.js CLI",
    subtitle: "YAML-driven web scraping CLI in Node.js and TypeScript.",
    description:
      "A command-line scraper that turns a small YAML config into a working extraction pipeline. Handles both static pages via Cheerio and JavaScript-heavy pages via Puppeteer, including list-to-detail navigation and infinite scroll.",
    technologies: ["Node.js", "TypeScript", "Oclif", "Cheerio", "Puppeteer"],
    features: [
      "Static + dynamic scraping",
      "YAML configuration",
      "List-to-detail navigation",
      "Infinite scroll support",
      "Regex-based formatting"
    ],
    github: "https://github.com/goodone-dev/thief-cli",
    image: "https://opengraph.githubassets.com/b583965fcca90f766c06d9c0272222e6c7b287fd42b4360d64b0c3279cb80ba1/goodone-dev/thief-cli"
  }
];

export const socialLinks = [
  { name: "GitHub", url: "https://github.com/goodone-dev", icon: "Github" },
  { name: "LinkedIn", url: "https://www.linkedin.com/in/BagusAK95", icon: "Linkedin" },
  { name: "Email", url: "mailto:hello@goodone.dev", icon: "Mail" }
];